import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { activityFeedApi } from '../utils/activityFeedApi';
import LayoutHeader from '../layout/LayoutHeader';

const ActivityItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: ${props => props.hoverColor || '#17A2B8'};
    background: ${props => props.hoverColor ? `${props.hoverColor}0D` : 'rgba(23, 162, 184, 0.05)'};
  }
`;

const ActivityContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ActivityTitle = styled.div`
  font-size: 12px;
  color: white;
  font-weight: 600;
`;

const ActivityDescription = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  margin-bottom: 8px;
`;

const ActivityTime = styled.div`
  font-size: 9px;
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
`;

const ActivityMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 9px;
  color: rgba(255, 255, 255, 0.5);
  flex-wrap: wrap;
`;

const MetaTag = styled.span`
  background: ${props => props.color ? `${props.color}33` : 'rgba(23, 162, 184, 0.2)'};
  color: ${props => props.color || 'rgba(23, 162, 184, 1)'};
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
`;

const LoadingContainer = styled.div`
  color: white;
  text-align: center;
  padding: 60px 0;
  font-size: 12px;
`;

const EmptyState = styled.div`
  text-align: left;
  padding: 60px 0;
`;

const EmptyStateTitle = styled.h3`
  color: white;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const EmptyStateDescription = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 24px;
  max-width: 300px;
`;

const EmptyStateButton = styled.button`
  background: #17A2B8;
  color: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 12px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #20B2AA;
  }
`;

const SimpleEmptyState = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 0;
`;

const EmptyStateText = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
`;

const TabsContainer = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 0 16px; /* Match header px-4 padding on both sides */
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ActivityFeedPage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadActivityFeed();
  }, []);

  const loadActivityFeed = async () => {
    try {
      setLoading(true);
      const response = await activityFeedApi.getActivityFeed();
      
      if (response && response.success && Array.isArray(response.activities)) {
        setActivities(response.activities);
      } else if (response && Array.isArray(response.activities)) {
        setActivities(response.activities);
      } else {
        setActivities([]);
      }
    } catch (error) {
      console.error('Failed to load activity feed:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredActivities = () => {
    if (!Array.isArray(activities)) {
      return [];
    }
    
    let filtered = activities;
    
    // Filter by type
    if (activeFilter !== 'all') {
      filtered = filtered.filter(activity => activity.type === activeFilter);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(activity => 
        activity.title?.toLowerCase().includes(query) ||
        activity.description?.toLowerCase().includes(query) ||
        activity.type?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const getEmptyStateContent = () => {
    const emptyStates = {
      all: {
        title: 'No recent activity',
        description: 'Start using your apps to see activity here',
        buttonText: 'Get Started',
        action: () => navigate('/main')
      },
      bookings: {
        text: 'No booking data — add your events to check back here'
      },
      catalog: {
        text: 'No catalog items — add music to see it here'
      },
      merch: {
        text: 'Connect your merch store to start tracking sales'
      },
      social: {
        text: 'Sync your social accounts to see new mentions here'
      },
      playlist: {
        text: 'No playlists — create your first playlist to see it here'
      },
      charting: {
        text: 'No chart data — connect your music to see chart positions'
      },
      playlisting: {
        text: 'No playlist data — track your songs in playlists'
      },
      'social-listening': {
        text: 'No social mentions — connect your accounts to monitor buzz'
      },
      'spotify-revenue': {
        text: 'No revenue data — add your Spotify streams to calculate earnings'
      }
    };

    return emptyStates[activeFilter] || emptyStates.all;
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - activityTime) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}h`;
    } else {
      return `${Math.floor(diffInSeconds / 86400)}d`;
    }
  };

  const renderActivityItem = (activity) => {
    const activityColor = getTypeColor(activity.type);
    
    return (
      <ActivityItem key={activity.id} hoverColor={activityColor}>
        <ActivityContent>
          <ActivityHeader>
            <ActivityTitle>{activity.title}</ActivityTitle>
            <ActivityTime>{formatTimeAgo(activity.timestamp)}</ActivityTime>
          </ActivityHeader>
          <ActivityDescription>{activity.description}</ActivityDescription>
          {activity.meta && (
            <ActivityMeta>
              <MetaTag color={getTypeColor(activity.type)}>{activity.type}</MetaTag>
              {activity.meta}
            </ActivityMeta>
          )}
        </ActivityContent>
      </ActivityItem>
    );
  };

  const getTypeColor = (type) => {
    const colors = {
      bookings: '#FFFF00',
      catalog: '#17A2B8',
      merch: '#9932CC',
      social: '#FF6B6B',
      playlist: '#34C759',
      charting: '#FF9500',
      playlisting: '#5856D6',
      'social-listening': '#007AFF',
      'spotify-revenue': '#1DB954',
      reminders: '#FF3B30'
    };
    return colors[type] || '#8E8E93';
  };

  const getTabColor = (tabId) => {
    const tabColors = {
      all: '#FF2300',
      bookings: '#FFFF00',
      catalog: '#17A2B8',
      merch: '#9932CC',
      social: '#FF6B6B',
      playlist: '#34C759',
      charting: '#FF9500',
      playlisting: '#5856D6',
      'social-listening': '#007AFF',
      'spotify-revenue': '#1DB954',
      reminders: '#FF3B30'
    };
    return tabColors[tabId] || '#8E8E93';
  };

  const filteredActivities = getFilteredActivities();

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div 
            className="w-[390px] flex flex-col items-center"
            style={{
              height: 'calc(100vh - 80px)',
              maxHeight: 'calc(100vh - 80px)',
              overflowY: 'scroll',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <LoadingContainer>Loading activity feed...</LoadingContainer>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div 
          className="w-[390px] flex flex-col items-center relative z-[0]"
          style={{
            height: 'calc(100vh - 80px)',
            maxHeight: 'calc(100vh - 80px)',
            overflowY: 'scroll',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: '100px'
          }}
        >

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <p className="text-white text-[12px]">Activity Feed</p>
            <div className="flex items-center gap-4">
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Search Bar */}
          <div className="w-full px-4 mb-6">
            <div className="flex items-center gap-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-[rgba(255,255,255,0.5)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
              </svg>
              <input
                type="text"
                placeholder="Search activity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] outline-none flex-1"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <TabsContainer>
              {[
                { id: 'all', label: 'All' },
                { id: 'bookings', label: 'Bookings' },
                { id: 'catalog', label: 'Catalog' },
                { id: 'merch', label: 'Merch' },
                { id: 'social', label: 'Social' },
                { id: 'playlist', label: 'Playlist' },
                { id: 'charting', label: 'Charting' },
                { id: 'playlisting', label: 'Playlisting' },
                { id: 'social-listening', label: 'Social Listening' },
                { id: 'spotify-revenue', label: 'Spotify Revenue' },
                { id: 'reminders', label: 'Reminders' }
              ].map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveFilter(tab.id)}
                  className={`flex-shrink-0 px-4 py-3 text-[12px] font-normal border-b-2 transition-all whitespace-nowrap ${
                    activeFilter === tab.id
                      ? 'text-white'
                      : 'text-[rgba(255,255,255,0.5)] border-transparent hover:text-white'
                  }`}
                  style={{
                    borderBottomColor: activeFilter === tab.id ? getTabColor(tab.id) : 'transparent'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </TabsContainer>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1">
            {filteredActivities.length === 0 ? (
              activeFilter === 'all' ? (
                <EmptyState>
                  <EmptyStateTitle>{getEmptyStateContent().title}</EmptyStateTitle>
                  <EmptyStateDescription>{getEmptyStateContent().description}</EmptyStateDescription>
                </EmptyState>
              ) : (
                <SimpleEmptyState>
                  <EmptyStateText>{getEmptyStateContent().text}</EmptyStateText>
                </SimpleEmptyState>
              )
            ) : (
              <div className="grid gap-4">
                {filteredActivities.map(renderActivityItem)}
              </div>
            )}
          </div>

          {/* Help Text */}
          <div className="w-full px-4 mt-6 mb-4">
            <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
              <p>View and manage all your recent activity across apps.</p>
              <p>Switch between tabs to filter activities by type or view all recent updates.</p>
            </div>
          </div>

        </div>
      </div>
    </LayoutHeader>
  );
};

export default ActivityFeedPage; 